import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Chip, CircularProgress, Typography,
} from '@mui/material';
import axios from 'axios';
import type { AxiosResponse } from 'axios';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useSelector } from 'react-redux';
import type { RootState } from 'mainStore';
import PrimayButton from 'shared-components/components/PrimayButton';
import StradaLoader from 'shared-components/components/StradaLoader';
import { SharePointIcon } from '../integrationIcons';
import FolderPickerDialog from './FolderPickerDialog';
import { connectResult, requestError } from './messages';
import ReviewSection from './ReviewSection';
import type { ApiData, SharePointStatus } from './types';

const API = 'api/integrations/sharepoint';

// The first walk of a newly chosen folder: nothing to show until it finishes.
function isFirstWalk(status: SharePointStatus | undefined): boolean {
  return status?.connected === true && status.status !== 'needs_reconnect' && (status.root_path ?? '') !== ''
    && (status.last_synced_at ?? null) === null;
}

// Poll quickly while Strada is reading SharePoint or a lease, and often enough otherwise to keep the page current.
function pollInterval(status: SharePointStatus | undefined): number {
  if (isFirstWalk(status) || status?.syncing === true) {
    return 5000;
  }
  return (status?.processing ?? 0) > 0 ? 10000 : 15000;
}

// Everything the lists below depend on. When any of it changes they reload, so nobody has to refresh the browser.
function statusVersion(status: SharePointStatus | undefined): string | null {
  if (status === undefined) {
    return null;
  }
  return JSON.stringify([status.last_synced_at, status.files, status.buildings_to_confirm, status.changed_in_sharepoint,
    status.possible_move_outs, status.possible_replacements, status.processing]);
}

export default function SharePointIntegration(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const workspace = useSelector((state: RootState) => state.workspaces.currentWorkspace.currentWorkspace);
  const [pickerOpen, setPickerOpen] = useState(false);

  // Coming back from Microsoft: ?sharepoint=connected or ?sharepoint=error&reason=…
  useEffect(() => {
    const url = new URL(window.location.href);
    const result = connectResult(url.searchParams.get('sharepoint'), url.searchParams.get('reason'));
    if (result === null) {
      return;
    }
    enqueueSnackbar(result.text, { variant: result.variant });
    url.searchParams.delete('sharepoint');
    url.searchParams.delete('reason');
    window.history.replaceState({}, document.title, url.toString());
    if (result.variant === 'success') {
      setPickerOpen(true);
    }
  }, [enqueueSnackbar]);

  const { data: status } = useQuery(
    ['get/sharepoint-status', workspace.id],
    async () => axios({ url: `${API}/status/`, method: 'get', params: { workspace: workspace.id } }),
    {
      enabled: workspace.id > 0,
      select: (res: AxiosResponse<ApiData<SharePointStatus>>) => res.data.data,
      refetchInterval: pollInterval,
      // Coming back from SharePoint in another browser tab shows what changed right away.
      refetchOnWindowFocus: true,
    },
  );

  const version = statusVersion(status);
  const seenVersion = useRef<string | null>(null);
  useEffect(() => {
    if (version === null) {
      return;
    }
    if (seenVersion.current !== null && seenVersion.current !== version) {
      Promise.all([
        queryClient.invalidateQueries('get/sharepoint-items'),
        queryClient.invalidateQueries('get/sharepoint-buildings'),
        queryClient.invalidateQueries('get/sharepoint-move-outs'),
      ]).catch((): void => { /* the next change reloads them */ });
    }
    seenVersion.current = version;
  }, [version, queryClient]);

  const { mutate: connect, isLoading: connecting } = useMutation(
    async () => axios({ url: `${API}/connect/`, method: 'get', params: { workspace: workspace.id } }),
    {
      onSuccess: (res: AxiosResponse<ApiData<{ login_url: string }>>): void => {
        window.location.href = res.data.data.login_url;
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'connect SharePoint'), { variant: 'error' }); },
    },
  );
  const { mutate: syncNow, isLoading: queueingSync } = useMutation(
    async () => axios({ url: `${API}/sync/`, method: 'post', params: { workspace: workspace.id } }),
    {
      onSuccess: async (): Promise<void> => {
        enqueueSnackbar('Sync started. New files will show up here in a few minutes.');
        await queryClient.invalidateQueries('get/sharepoint-status');
      },
      onError: (error: unknown): void => { enqueueSnackbar(requestError(error, 'sync SharePoint'), { variant: 'error' }); },
    },
  );

  const connected = status?.connected === true;
  const needsReconnect = status?.status === 'needs_reconnect';
  const rootPath = status?.root_path ?? '';
  const firstWalk = isFirstWalk(status);
  const files = status?.files ?? {};

  const actions = (): JSX.Element => {
    if (needsReconnect) {
      return <PrimayButton onClick={(): void => { connect(); }}>Reconnect</PrimayButton>;
    }
    if (!connected) {
      // Same fixed-width wrapper as the Gmail and Outlook Connect buttons.
      return <div className='btn-wrapper'><PrimayButton onClick={(): void => { connect(); }}>Connect</PrimayButton></div>;
    }
    if (rootPath === '') {
      return <PrimayButton onClick={(): void => { setPickerOpen(true); }}>Choose folder</PrimayButton>;
    }
    return (
      <Box sx={{
        display: 'flex', gap: 2, alignItems: 'center', whiteSpace: 'nowrap',
      }}
      >
        {/* Outlined twin of PrimayButton: same height, font and green (theme primary = $color-green). */}
        <Button
          variant='outlined'
          color='primary'
          onClick={(): void => { setPickerOpen(true); }}
          sx={{
            height: 36, px: 2.5, flexShrink: 0, textTransform: 'capitalize', fontWeight: 500, fontSize: 14, letterSpacing: '0.1px',
          }}
        >
          Change folder
        </Button>
        {/* PrimayButton is full width by default, which squeezed the button beside it. */}
        <PrimayButton fullWidth={false} sx={{ px: 3, flexShrink: 0 }} onClick={(): void => { syncNow(); }}>Sync now</PrimayButton>
      </Box>
    );
  };

  return (
    <>
      <StradaLoader open={connecting || queueingSync} />
      <div className='single-integration'>
        <div className='left-side'>
          <div className='icon-wrapper'><SharePointIcon /></div>
          <div className='text-side'>
            <h6>SharePoint</h6>
            <p>Read leases straight from your SharePoint folders. Strada only reads; it never changes your files.</p>
          </div>
        </div>
        <div className='right-side'>{actions()}</div>
      </div>

      {connected && (
        // Indented to line up with the text beside the 40px icon (+16px gap), like the cards above.
        <Box sx={{ pl: '56px', pb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant='body2' color='text.secondary'>
              {`Connected as ${status.microsoft_email ?? ''}`}
              {rootPath !== '' && ` · Folder: ${rootPath}`}
              {status.last_synced_at !== null && status.last_synced_at !== undefined && ` · Last synced ${moment(status.last_synced_at).fromNow()}`}
              {status.syncing === true && !firstWalk && ' · Syncing…'}
            </Typography>
            {status.syncing === true && !firstWalk && <CircularProgress size={12} />}
          </Box>
          {needsReconnect && (
            <Typography variant='body2' color='error' sx={{ mt: 1 }}>
              The Microsoft sign-in has expired. Click Reconnect to keep SharePoint in sync.
            </Typography>
          )}
          {firstWalk && (
            <Box sx={{
              display: 'flex', alignItems: 'center', gap: 1, mt: 1,
            }}
            >
              <CircularProgress size={16} />
              <Typography variant='body2'>
                Reading your SharePoint folder and matching files to your leases… this can take a few minutes.
              </Typography>
            </Box>
          )}
          {rootPath !== '' && !firstWalk && (
            <>
              <Box sx={{
                display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1,
              }}
              >
                <Chip size='small' color='success' label={`${files.linked ?? 0} linked to leases`} />
                <Chip size='small' color='primary' label={`${files.imported ?? 0} imported`} />
                <Chip size='small' color='warning' label={`${files.unmatched ?? 0} to review`} />
                <Chip size='small' label={`${files.skipped ?? 0} other files`} />
                {(status.processing ?? 0) > 0 && (
                  <Chip size='small' icon={<CircularProgress size={12} color='inherit' />} label={`${status.processing ?? 0} processing`} />
                )}
                {(status.changed_in_sharepoint ?? 0) > 0 && (
                  <Chip size='small' color='info' label={`${status.changed_in_sharepoint ?? 0} changed in SharePoint`} />
                )}
                {(status.possible_move_outs ?? 0) > 0 && (
                  <Chip size='small' color='warning' label={`${status.possible_move_outs ?? 0} tenant may have left`} />
                )}
                {(status.possible_replacements ?? 0) > 0 && (
                  <Chip size='small' color='warning' label={`${status.possible_replacements ?? 0} possible corrected copy`} />
                )}
              </Box>
              <ReviewSection
                workspaceId={workspace.id}
                changedCount={status.changed_in_sharepoint ?? 0}
                moveOutCount={status.possible_move_outs ?? 0}
                replacementCount={status.possible_replacements ?? 0}
                importAll={status.import_all}
              />
            </>
          )}
        </Box>
      )}

      <FolderPickerDialog
        open={pickerOpen}
        workspaceId={workspace.id}
        onClose={(): void => { setPickerOpen(false); }}
        onChosen={(): void => {
          setPickerOpen(false);
          queryClient.invalidateQueries('get/sharepoint-status').catch((): void => { /* the next poll refreshes it */ });
        }}
      />
    </>
  );
}
